import React, { useEffect, useState, useRef } from 'react';
import {
  Wrapper,
  Status,
} from '@googlemaps/react-wrapper';
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  Close,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatPropertyAddress } from '../utils/propertyDisplay';

// Google Maps JS API key from environment (browser-exposed; restrict by HTTP referrers in GCP)
// Prefer the dedicated Places/Maps browser key.
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  process.env.REACT_APP_GEOCODING_KEY ||
  process.env.REACT_APP_GEOCODING_API_KEY;

// Status colors for markers
const statusColors = {
  'available': '#28a745',
  'on-hold': '#dc3545',
  'first come first serve': '#17a2b8',
  'vacant on lockbox': '#008080',
  'under contract': '#ffc107',
  'pending': '#6f42c1',
  'sold': '#fd7e14',
};

// Google Maps Component
const GoogleMap = ({
  center,
  zoom,
  properties,
  onViewProperty,
  initialFitMode = 'allMarkers', // 'allMarkers' | 'firstPropertyRadius'
  initialRadiusMiles = 30,
  initialFitProperty, // { latitude, longitude, id? }
}) => {
  const ref = useRef();
  const [map, setMap] = useState();
  const lastInitialFitKeyRef = useRef(null);

  useEffect(() => {
    if (!ref.current || !window.google) return;

    const newMap = new window.google.maps.Map(ref.current, {
      center,
      zoom,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    setMap(newMap);
  }, [center, zoom]);

  // Create markers when map is ready and properties change
  useEffect(() => {
    if (!map || !properties.length) return;

    // Clear existing markers
    // Note: In a real implementation, you'd track markers to clean them up

    // Create markers for properties with coordinates
    const validProperties = properties.filter(p => p.latitude && p.longitude);

    validProperties.forEach((property) => {
      const addressLabel = formatPropertyAddress(property);
      const normalizedStatus = property.status.toLowerCase().replace(/\s+/g, ' ');
      const color = statusColors[normalizedStatus] || '#6c757d';
      const priceNumber = typeof property.price === 'string' ? Number(property.price) : property.price;
      const priceLabel = Number.isFinite(priceNumber)
        ? `$${(priceNumber / 1000).toFixed(0)}K`
        : 'N/A';

      const marker = new window.google.maps.Marker({
        position: { lat: property.latitude, lng: property.longitude },
        map,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg width="48" height="60" viewBox="0 0 48 60" xmlns="http://www.w3.org/2000/svg">
              <path d="M24 0C10.745 0 0 10.745 0 24c0 13.255 24 36 24 36s24-22.745 24-36C48 10.745 37.255 0 24 0z" fill="${color}" stroke="white" stroke-width="2"/>
              <circle cx="24" cy="22" r="16" fill="white"/>
              <text x="24" y="27" text-anchor="middle" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="${color}">${priceLabel}</text>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(48, 60),
          anchor: new window.google.maps.Point(24, 60),
        },
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="max-width: 280px; font-family: Roboto, Arial, sans-serif;">
            <h3 style="margin: 0 0 8px 0; color: #008080; font-size: 16px;">${addressLabel}</h3>
            <p style="margin: 0 0 8px 0; font-size: 18px; font-weight: bold; color: #008080;">
              $${property.price ? property.price.toLocaleString() : 'N/A'}
            </p>
            <div style="display: flex; gap: 4px; margin-bottom: 8px; flex-wrap: wrap;">
              ${property.bedrooms ? `<span style="background: #f0f0f0; padding: 2px 6px; border-radius: 4px; font-size: 12px;">${property.bedrooms}BR</span>` : ''}
              ${property.bathrooms ? `<span style="background: #f0f0f0; padding: 2px 6px; border-radius: 4px; font-size: 12px;">${property.bathrooms}BA</span>` : ''}
              ${property.squareFootage ? `<span style="background: #f0f0f0; padding: 2px 6px; border-radius: 4px; font-size: 12px;">${property.squareFootage} sqft</span>` : ''}
              ${property.propertyType ? `<span style="background: #f0f0f0; padding: 2px 6px; border-radius: 4px; font-size: 12px;">${property.propertyType}</span>` : ''}
            </div>
            <span style="background: ${color}; color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold;">
              ${property.status}
            </span>
            <br><br>
            <button onclick="window.dispatchEvent(new CustomEvent('viewProperty', { detail: '${property.id}' }))"
                    style="background: #008080; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 14px;">
              View Details
            </button>
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });
    });

    // Initial "default zoom" behavior
    if (initialFitMode === 'firstPropertyRadius' && initialFitProperty?.latitude && initialFitProperty?.longitude) {
      const radiusMeters = Number(initialRadiusMiles) * 1609.344;
      const fitKey = `firstPropertyRadius:${initialFitProperty.id ?? ''}:${initialFitProperty.latitude},${initialFitProperty.longitude}:${radiusMeters}`;

      if (lastInitialFitKeyRef.current !== fitKey) {
        const circle = new window.google.maps.Circle({
          center: { lat: initialFitProperty.latitude, lng: initialFitProperty.longitude },
          radius: radiusMeters,
        });
        const bounds = circle.getBounds();
        if (bounds) {
          map.fitBounds(bounds);
          lastInitialFitKeyRef.current = fitKey;
        }
      }
    } else if (validProperties.length > 0) {
      // Default: fit bounds to show all markers
      const bounds = new window.google.maps.LatLngBounds();
      validProperties.forEach(property => {
        bounds.extend({ lat: property.latitude, lng: property.longitude });
      });
      map.fitBounds(bounds);
    }
  }, [map, properties, onViewProperty, initialFitMode, initialRadiusMiles, initialFitProperty]);

  // Listen for custom events from info windows
  useEffect(() => {
    const handleViewProperty = (event) => {
      onViewProperty(event.detail);
    };

    window.addEventListener('viewProperty', handleViewProperty);
    return () => window.removeEventListener('viewProperty', handleViewProperty);
  }, [onViewProperty]);

  return <div ref={ref} style={{ height: '100%', width: '100%' }} />;
};

const MapStatus = ({ status }) => {
  if (status === Status.LOADING) return <CircularProgress />;
  if (status === Status.FAILURE) return <Typography>Error loading map</Typography>;
  return null;
};

// Wrapper component that handles Google Maps loading
const GoogleMapsWrapper = ({ children }) => {
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Google Maps API key not configured
        </Typography>
      </Box>
    );
  }

  return (
    <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={MapStatus}>
      {children}
    </Wrapper>
  );
};

export default function PropertyMap({
  properties,
  onClose,
  compact = false,
  onViewProperty,
  initialFitMode = 'allMarkers', // 'allMarkers' | 'firstPropertyRadius'
  initialRadiusMiles = 30,
}) {
  const navigate = useNavigate();

  // Default center (San Diego, CA)
  const defaultCenter = { lat: 32.7157, lng: -117.1611 };
  const defaultZoom = 10;

  // Filter properties that have coordinates
  const propertiesWithCoords = properties?.filter(p => p.latitude && p.longitude) || [];
  const firstListedPropertyWithCoords = (properties || []).find(p => p?.latitude && p?.longitude) || null;

  // Legend data
  const legendItems = [
    { status: 'Available', color: '#28a745' },
    { status: 'Under Contract', color: '#ffc107' },
    { status: 'On-Hold', color: '#dc3545' },
    { status: 'First Come First Serve', color: '#17a2b8' },
    { status: 'Vacant on Lockbox', color: '#008080' },
    { status: 'Pending', color: '#6f42c1' },
    { status: 'Sold', color: '#fd7e14' },
    // Fallback color used for unknown/legacy statuses
    { status: 'Other', color: '#6c757d' },
  ];

  const handleViewProperty = (propertyId) => {
    if (onViewProperty) {
      onViewProperty(propertyId);
    } else {
      navigate(`/property/${propertyId}`);
    }
  };

  if (!properties || properties.length === 0) {
    return (
      <Box
        sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Loading properties...
        </Typography>
      </Box>
    );
  }

  if (propertiesWithCoords.length === 0) {
    if (compact) {
      return (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            No properties with coordinates to display
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
        {onClose && (
          <AppBar position="static" sx={{ backgroundColor: '#008080' }}>
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Property Map (No properties with coordinates)
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="close"
              >
                <Close />
              </IconButton>
            </Toolbar>
          </AppBar>
        )}
        <Box sx={{ flex: 1, borderRadius: onClose ? 0 : 1, overflow: 'hidden' }}>
          <Typography sx={{ p: 2 }}>No properties with coordinates found.</Typography>
          <Typography sx={{ p: 2 }}>Properties received: {properties?.length || 0}</Typography>
          {properties?.slice(0, 3).map((p, i) => (
            <Typography key={i} sx={{ p: 1 }}>
              {formatPropertyAddress(p)}: {p.latitude}, {p.longitude}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  }

  if (compact) {
    return (
      <Box sx={{ height: '100%', width: '100%', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Compact Legend */}
        <Paper
          elevation={2}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1000,
            p: 1,
            maxWidth: 200,
            maxHeight: 220,
            overflowY: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ color: '#008080', fontSize: '0.75rem' }}>
            Legend
          </Typography>
          <Stack spacing={0.5}>
            {legendItems.map((item) => (
              <Box key={item.status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    border: '1px solid white',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.1 }}>
                  {item.status}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
        <GoogleMapsWrapper>
          <GoogleMap
            center={firstListedPropertyWithCoords ? { lat: firstListedPropertyWithCoords.latitude, lng: firstListedPropertyWithCoords.longitude } : defaultCenter}
            zoom={defaultZoom}
            properties={propertiesWithCoords}
            onViewProperty={handleViewProperty}
            initialFitMode={initialFitMode}
            initialRadiusMiles={initialRadiusMiles}
            initialFitProperty={firstListedPropertyWithCoords}
          />
        </GoogleMapsWrapper>
      </Box>
    );
  }

  // Render full modal map (default)
  return (
    <Box sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {onClose && (
        <AppBar position="static" sx={{ backgroundColor: '#008080' }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Property Map ({propertiesWithCoords.length} properties)
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      <Box sx={{ flex: 1, borderRadius: onClose ? 0 : 1, overflow: 'hidden', position: 'relative' }}>
        {/* Legend */}
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            p: 2,
            maxWidth: 280,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', fontSize: '1rem' }}>
            Property Status Legend
          </Typography>
          <Stack spacing={1}>
            {legendItems.map((item) => (
              <Box key={item.status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
                  {item.status}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
        <GoogleMapsWrapper>
          <GoogleMap
            center={firstListedPropertyWithCoords ? { lat: firstListedPropertyWithCoords.latitude, lng: firstListedPropertyWithCoords.longitude } : defaultCenter}
            zoom={defaultZoom}
            properties={propertiesWithCoords}
            onViewProperty={handleViewProperty}
            initialFitMode={initialFitMode}
            initialRadiusMiles={initialRadiusMiles}
            initialFitProperty={firstListedPropertyWithCoords}
          />
        </GoogleMapsWrapper>
      </Box>
    </Box>
  );
}
